import type { ReactNode } from 'react';
import type { IconName } from '@/content/icons';
import { Icon } from './Icon';

/** 抬起来的面板：页面底比卡片底深一档，卡片因此看起来是浮在上面的。 */
export function Card({
  children,
  className,
  interactive = false,
  id,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
  id?: string;
  as?: 'div' | 'li' | 'article';
}) {
  return (
    <Tag
      id={id}
      className={[
        'card-surface p-5 md:p-6',
        interactive ? 'card-hover' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </Tag>
  );
}

/** 图标 + 标题 + 说明，首页能力、平台模块、价值点都用它。 */
export function FeatureCard({
  icon,
  title,
  text,
  eyebrow,
  footer,
  className,
}: {
  icon: IconName;
  title: string;
  text: string;
  eyebrow?: string;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <Card interactive className={['flex flex-col gap-3', className].filter(Boolean).join(' ')}>
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-grad-tint text-brand">
        <Icon name={icon} size={20} />
      </span>
      {eyebrow ? <span className="label-mono">{eyebrow}</span> : null}
      <h3 className="text-base font-semibold text-fg">{title}</h3>
      <p className="text-sm leading-relaxed text-fg-muted">{text}</p>
      {footer ? <div className="mt-auto pt-1">{footer}</div> : null}
    </Card>
  );
}

/** 带标记的要点列表，样式在 prose 之外复用（方案页、接入说明）。 */
export function CheckList({
  items,
  icon = 'check',
  className,
}: {
  items: readonly string[];
  icon?: IconName;
  className?: string;
}) {
  return (
    <ul className={['space-y-2.5', className].filter(Boolean).join(' ')}>
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-fg-muted">
          <Icon name={icon} size={16} className="mt-0.5 text-fg-subtle" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** 强调块：把一句需要被记住的话单独拎出来。 */
export function Callout({
  children,
  label,
  icon = 'info',
  className,
}: {
  children: ReactNode;
  label?: string;
  icon?: IconName;
  className?: string;
}) {
  return (
    <div
      className={[
        'flex gap-3 rounded-xl border border-line bg-surface-1 bg-grad-soft p-4 text-sm leading-relaxed text-fg',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon name={icon} size={18} className="mt-0.5 text-brand" />
      <div>
        {label ? <p className="mb-1 font-semibold text-fg">{label}</p> : null}
        <div className="text-fg-muted">{children}</div>
      </div>
    </div>
  );
}
